import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

const themes = {
  // ── DARK THEMES ──────────────────────────────────────────

  obsidian: {
    name: 'Obsidian',
    group: 'dark',
    shipped: true,
    colors: {
      '--color-obsidian': '10 14 26',
      '--color-charcoal': '19 24 37',
      '--bg-primary': '#0A0E1A',
      '--bg-secondary': '#131825',
      '--bg-card': '#131825',
      '--border': 'rgba(255,255,255,0.06)',
      '--accent': '#38BDF8',
      '--accent-rgb': '56,189,248',
      '--accent-hover': '#22D3EE',
      '--text-primary': '#FFFFFF',
      '--text-secondary': '#94A3B8',
      '--text-muted': '#64748B',
      '--sidebar-bg': '#131825',
      '--sidebar-active': 'rgba(56,189,248,0.1)',
      '--sidebar-active-text': '#38BDF8',
    },
    preview: 'bg-[#0A0E1A]',
    dot: 'bg-sky-400',
  },

  aurora: {
    name: 'Aurora',
    group: 'dark',
    colors: {
      '--color-obsidian': '10 26 26',
      '--color-charcoal': '15 43 43',
      '--bg-primary': '#0A1A1A',
      '--bg-secondary': '#0F2B2B',
      '--bg-card': '#0F2B2B',
      '--border': 'rgba(52,211,153,0.1)',
      '--accent': '#34D399',
      '--accent-rgb': '52,211,153',
      '--accent-hover': '#6EE7B7',
      '--text-primary': '#F0FDF4',
      '--text-secondary': '#86EFAC',
      '--text-muted': '#4ADE80',
      '--sidebar-bg': '#0F2B2B',
      '--sidebar-active': 'rgba(52,211,153,0.1)',
      '--sidebar-active-text': '#34D399',
    },
    preview: 'bg-[#0A1A1A]',
    dot: 'bg-emerald-400',
  },

  ember: {
    name: 'Ember',
    group: 'dark',
    colors: {
      '--color-obsidian': '18 12 12',
      '--color-charcoal': '28 20 20',
      '--bg-primary': '#120C0C',
      '--bg-secondary': '#1C1414',
      '--bg-card': '#1C1414',
      '--border': 'rgba(220,120,95,0.1)',
      '--accent': '#E8836A',
      '--accent-rgb': '232,131,106',
      '--accent-hover': '#F0A08A',
      '--text-primary': '#F5EDE8',
      '--text-secondary': '#B0948A',
      '--text-muted': '#7A6058',
      '--sidebar-bg': '#1C1414',
      '--sidebar-active': 'rgba(232,131,106,0.1)',
      '--sidebar-active-text': '#E8836A',
    },
    preview: 'bg-[#120C0C]',
    dot: 'bg-[#E8836A]',
  },

  nightowl: {
    name: 'Night Owl',
    group: 'dark',
    colors: {
      '--color-obsidian': '1 22 39',
      '--color-charcoal': '3 32 53',
      '--bg-primary': '#011627',
      '--bg-secondary': '#032035',
      '--bg-card': '#032035',
      '--border': 'rgba(127,170,210,0.1)',
      '--accent': '#82AAFF',
      '--accent-rgb': '130,170,255',
      '--accent-hover': '#A4C4FF',
      '--text-primary': '#D6DEEB',
      '--text-secondary': '#8BADC4',
      '--text-muted': '#5C7D95',
      '--sidebar-bg': '#032035',
      '--sidebar-active': 'rgba(130,170,255,0.08)',
      '--sidebar-active-text': '#82AAFF',
    },
    preview: 'bg-[#011627]',
    dot: 'bg-blue-300',
  },

  glass: {
    name: 'Liquid Glass',
    group: 'light',
    special: 'glass',
    colors: {
      '--color-obsidian': '218 225 238',
      '--color-charcoal': '226 232 244',
      '--bg-primary': '#DAE1EE',
      '--bg-secondary': '#E2E8F4',
      '--bg-card': '#E2E8F4',
      '--border': 'rgba(0,0,0,0.08)',
      // Darkened 6 Jun 2026: #4A7FD4 read 3.24:1 as label ink on Glass's own
      // pale card and only 3.98:1 under a white label — the accent failed AA in
      // both directions, which the dialog audit surfaced. Same blue, deeper.
      '--accent': '#2C5AA0',
      '--accent-rgb': '44,90,160',
      '--accent-hover': '#234A85',
      '--text-primary': '#0F172A',
      '--text-secondary': '#334155',
      // #64748B was 3.87:1 on Glass's frosted card — the command palette's
      // footer hints ("navigate", "open", "esc") were below AA.
      '--text-muted': '#556377',
      '--sidebar-bg': '#0a0a0c',
      '--sidebar-active': 'rgba(74,127,212,0.15)',
      '--sidebar-active-text': '#7EB8F0',
    },
    preview: 'bg-[#DAE1EE]',
    dot: 'bg-[#4A7FD4]',
  },

  gotham: {
    name: 'Gotham',
    group: 'dark',
    shipped: true,
    colors: {
      '--color-obsidian': '6 6 10',
      '--color-charcoal': '14 14 20',
      '--bg-primary': '#06060A',
      '--bg-secondary': '#0E0E14',
      '--bg-card': '#0E0E14',
      '--border': 'rgba(245,197,24,0.06)',
      '--accent': '#F5C518',
      '--accent-rgb': '245,197,24',
      '--accent-hover': '#FFD740',
      '--text-primary': '#E8E6E0',
      '--text-secondary': '#8A8880',
      '--text-muted': '#5A5850',
      '--sidebar-bg': '#0A0A10',
      '--sidebar-active': 'rgba(245,197,24,0.08)',
      '--sidebar-active-text': '#F5C518',
    },
    preview: 'bg-[#06060A]',
    dot: 'bg-yellow-400',
  },

  athena: {
    name: 'Athena',
    group: 'dark',
    colors: {
      '--color-obsidian': '8 10 22',
      '--color-charcoal': '14 18 36',
      '--bg-primary': '#080A16',
      '--bg-secondary': '#0E1224',
      '--bg-card': '#0E1224',
      '--border': 'rgba(212,168,67,0.08)',
      '--accent': '#D4A843',
      '--accent-rgb': '212,168,67',
      '--accent-hover': '#E8C060',
      '--text-primary': '#F0ECE0',
      '--text-secondary': '#A09880',
      '--text-muted': '#686050',
      '--sidebar-bg': '#0A0C1A',
      '--sidebar-active': 'rgba(212,168,67,0.1)',
      '--sidebar-active-text': '#D4A843',
    },
    preview: 'bg-[#080A16]',
    dot: 'bg-yellow-500',
  },

  graphite: {
    name: 'Graphite',
    group: 'dark',
    colors: {
      '--color-obsidian': '22 22 24',
      '--color-charcoal': '32 32 35',
      '--bg-primary': '#161618',
      '--bg-secondary': '#202023',
      '--bg-card': '#202023',
      '--border': 'rgba(255,255,255,0.06)',
      '--accent': '#A0A0A8',
      '--accent-rgb': '160,160,168',
      '--accent-hover': '#C0C0C8',
      '--text-primary': '#E8E8EC',
      '--text-secondary': '#8E8E96',
      '--text-muted': '#5A5A62',
      '--sidebar-bg': '#202023',
      '--sidebar-active': 'rgba(160,160,168,0.1)',
      '--sidebar-active-text': '#C0C0C8',
    },
    preview: 'bg-[#161618]',
    dot: 'bg-zinc-400',
  },

  sage: {
    name: 'Sage',
    group: 'light',
    shipped: true,
    lightSidebar: true,
    colors: {
      '--color-obsidian': '247 249 245',
      '--color-charcoal': '255 255 255',
      '--bg-primary': '#F7F9F5',
      '--bg-secondary': '#FFFFFF',
      '--bg-card': '#FFFFFF',
      '--border': '#E1E6DF',
      '--accent': '#2E5A44',
      '--accent-rgb': '46,90,68',
      '--accent-hover': '#214233',
      '--text-primary': '#1A241E',
      '--text-secondary': '#4B5A52',
      '--text-muted': '#65756E',
      '--sidebar-bg': '#F0F4EE',
      '--sidebar-active': '#E2EBE0',
      '--sidebar-active-text': '#2E5A44',
    },
    preview: 'bg-[#F7F9F5]',
    dot: 'bg-[#2E5A44]',
  },

  glacier: {
    name: 'Glacier',
    group: 'light',
    shipped: true,
    lightSidebar: true,
    colors: {
      '--color-obsidian': '244 248 250',
      '--color-charcoal': '255 255 255',
      '--bg-primary': '#F4F8FA',
      '--bg-secondary': '#FFFFFF',
      '--bg-card': '#FFFFFF',
      '--border': '#D8E3E8',
      '--accent': '#2B6CB0',
      '--accent-rgb': '43,108,176',
      '--accent-hover': '#1C4ED8',
      '--text-primary': '#0F1A24',
      '--text-secondary': '#415566',
      '--text-muted': '#5C7185',
      '--sidebar-bg': '#EBF2F5',
      '--sidebar-active': '#DCEDF5',
      '--sidebar-active-text': '#2B6CB0',
    },
    preview: 'bg-[#F4F8FA]',
    dot: 'bg-[#2B6CB0]',
  },

  // MEADOW — the DAY theme (owner, 5 Sep 2026: "a lighter version which feels
  // light, of course a notch darker than white"). Sage's page is #F7F9F5, all
  // but white; Meadow's is #E7EEE5, a clear notch down, with near-white cards
  // sitting ON it so surfaces read as layers instead of one flat sheet. Same
  // green family as Sage / Moss / Cypress, so a team can mix light and dark
  // seats and still be on one palette.
  meadow: {
    name: 'Meadow',
    group: 'light',
    shipped: true,
    lightSidebar: true,
    colors: {
      '--color-obsidian': '231 238 229',
      '--color-charcoal': '247 250 246',
      '--bg-primary': '#E7EEE5',
      '--bg-secondary': '#F7FAF6',
      '--bg-card': '#F7FAF6',
      '--border': '#CEDACA',
      '--accent': '#2F7A55',
      '--accent-rgb': '47,122,85',
      '--accent-hover': '#24603F',
      '--text-primary': '#16241B',
      '--text-secondary': '#3E5145',
      '--text-muted': '#5B6E60',
      '--sidebar-bg': '#DFE8DC',
      '--sidebar-active': '#CFE0CF',
      '--sidebar-active-text': '#24603F',
    },
    preview: 'bg-[#E7EEE5]',
    dot: 'bg-[#2F7A55]',
  },

  // MOSS — the step the owner asked for on 5 Sep 2026: "darker than Sage but
  // lighter than Cypress". A DIM theme, not a dark one — the page is a mid
  // green-grey (#28322B) instead of near-black (#0B120E), so the screen stops
  // glowing without going to a hole. Same sage family as both, one step up in
  // accent brightness because a mid page needs more separation than a black one.
  moss: {
    name: 'Moss',
    group: 'dark',
    shipped: true,
    colors: {
      '--color-obsidian': '40 50 43',
      '--color-charcoal': '48 59 51',
      '--bg-primary': '#28322B',
      '--bg-secondary': '#303B33',
      '--bg-card': '#303B33',
      '--border': 'rgba(255, 255, 255, 0.10)',
      '--accent': '#7FBF9A',
      '--accent-rgb': '127,191,154',
      '--accent-hover': '#93CFAB',
      '--text-primary': '#EAF1EC',
      '--text-secondary': '#BFCFC4',
      '--text-muted': '#9DB0A4',
      '--sidebar-bg': '#232C26',
      '--sidebar-active': 'rgba(127, 191, 154, 0.18)',
      '--sidebar-active-text': '#93CFAB',
    },
    preview: 'bg-[#28322B]',
    dot: 'bg-[#7FBF9A]',
  },

  cypress: {
    name: 'Cypress',
    group: 'dark',
    shipped: true,
    colors: {
      '--color-obsidian': '11 18 14',
      '--color-charcoal': '18 28 22',
      '--bg-primary': '#0B120E',
      '--bg-secondary': '#121C16',
      '--bg-card': '#121C16',
      '--border': 'rgba(255, 255, 255, 0.08)',
      '--accent': '#529471',
      '--accent-rgb': '82,148,113',
      '--accent-hover': '#65A884',
      '--text-primary': '#EDF5F0',
      '--text-secondary': '#A3BCB0',
      '--text-muted': '#82A394',
      '--sidebar-bg': '#121C16',
      '--sidebar-active': 'rgba(82, 148, 113, 0.15)',
      '--sidebar-active-text': '#65A884',
    },
    preview: 'bg-[#0B120E]',
    dot: 'bg-[#529471]',
  },

  fjord: {
    name: 'Fjord',
    group: 'dark',
    shipped: true,
    colors: {
      '--color-obsidian': '8 14 24',
      '--color-charcoal': '15 23 42',
      '--bg-primary': '#080E18',
      '--bg-secondary': '#0F172A',
      '--bg-card': '#0F172A',
      '--border': 'rgba(255, 255, 255, 0.08)',
      '--accent': '#4A9EFF',
      '--accent-rgb': '74,158,255',
      '--accent-hover': '#60A5FA',
      '--text-primary': '#F0F6FF',
      '--text-secondary': '#94A3B8',
      '--text-muted': '#7B8EA3',
      '--sidebar-bg': '#0F172A',
      '--sidebar-active': 'rgba(74, 158, 255, 0.14)',
      '--sidebar-active-text': '#4A9EFF',
    },
    preview: 'bg-[#080E18]',
    dot: 'bg-[#4A9EFF]',
  },
  // ── LIGHT THEMES (Slack-inspired: dark sidebar stays dark, content area is colored) ──

  // DAYLIGHT — the flagship light theme, and the default for new users
  // (28 Aug 2026). Designed rather than inverted: white page, white cards, one
  // soft professional blue, and a LIGHT sidebar. Everything else it needs is
  // in styles/tokens.css under html[data-theme='daylight'].
  daylight: {
    name: 'Daylight',
    group: 'light',
    shipped: true,
    // The only theme with a LIGHT sidebar — the shell reads the flag rather
    // than the theme name, so a future light theme just sets it too.
    lightSidebar: true,
    colors: {
      '--color-obsidian': '255 255 255',
      '--color-charcoal': '250 250 250',
      '--bg-primary': '#FFFFFF',
      '--bg-secondary': '#FAFAFA',
      '--bg-card': '#FFFFFF',
      '--border': '#E4E4E7',
      '--accent': '#0E7490',
      '--accent-rgb': '14,116,144',
      '--accent-hover': '#0B5C73',
      '--text-primary': '#171717',
      '--text-secondary': '#52525B',
      '--text-muted': '#71717A',
      '--sidebar-bg': '#F9FAFB',
      '--sidebar-active': '#ECF7FB',
      '--sidebar-active-text': '#0B5C73',
    },
    preview: 'bg-white',
    dot: 'bg-[#0E7490]',
  },

  slate: {
    name: 'Slate',
    group: 'light',
    // Superseded by Daylight as the shipped light theme. Kept defined so
    // anyone already on it keeps it (shippedOnly() re-adds the active theme).
    shipped: false,
    colors: {
      '--color-obsidian': '188 192 200',
      '--color-charcoal': '196 200 208',
      '--bg-primary': '#BCC0C8',
      '--bg-secondary': '#C4C8D0',
      '--bg-card': '#C4C8D0',
      '--border': 'rgba(0,0,0,0.1)',
      '--accent': '#4A6382',
      '--accent-rgb': '74,99,130',
      '--accent-hover': '#3A5270',
      '--text-primary': '#111416',
      '--text-secondary': '#2A3038',
      '--text-muted': '#485058',
      '--sidebar-bg': '#0a0a0c',
      '--sidebar-active': 'rgba(56,189,248,0.1)',
      '--sidebar-active-text': '#38BDF8',
    },
    preview: 'bg-[#BCC0C8]',
    dot: 'bg-[#4A6382]',
  },

  clay: {
    name: 'Clay',
    group: 'light',
    colors: {
      '--color-obsidian': '200 188 172',
      '--color-charcoal': '208 196 182',
      '--bg-primary': '#C8BCAC',
      '--bg-secondary': '#D0C4B6',
      '--bg-card': '#D0C4B6',
      '--border': 'rgba(80,50,20,0.12)',
      '--accent': '#7A5C3C',
      '--accent-rgb': '122,92,60',
      '--accent-hover': '#664A2E',
      '--text-primary': '#1A1410',
      '--text-secondary': '#352C22',
      '--text-muted': '#52483C',
      '--sidebar-bg': '#0a0a0c',
      '--sidebar-active': 'rgba(122,92,60,0.15)',
      '--sidebar-active-text': '#D4A574',
    },
    preview: 'bg-[#C8BCAC]',
    dot: 'bg-[#7A5C3C]',
  },

  forest: {
    name: 'Forest',
    group: 'light',
    colors: {
      '--color-obsidian': '176 192 174',
      '--color-charcoal': '188 202 186',
      '--bg-primary': '#B0C0AE',
      '--bg-secondary': '#BCCABA',
      '--bg-card': '#BCCABA',
      '--border': 'rgba(20,60,20,0.1)',
      '--accent': '#3D6B45',
      '--accent-rgb': '61,107,69',
      '--accent-hover': '#2E5534',
      '--text-primary': '#0E1A0C',
      '--text-secondary': '#1E3018',
      '--text-muted': '#3A4E34',
      '--sidebar-bg': '#0a0a0c',
      '--sidebar-active': 'rgba(61,107,69,0.15)',
      '--sidebar-active-text': '#6EBF7A',
    },
    preview: 'bg-[#B0C0AE]',
    dot: 'bg-[#3D6B45]',
  },

  lagoon: {
    name: 'Lagoon',
    group: 'light',
    colors: {
      '--color-obsidian': '172 196 192',
      '--color-charcoal': '184 206 202',
      '--bg-primary': '#ACC4C0',
      '--bg-secondary': '#B8CECA',
      '--bg-card': '#B8CECA',
      '--border': 'rgba(20,60,55,0.1)',
      '--accent': '#2D7568',
      '--accent-rgb': '45,117,104',
      '--accent-hover': '#1E5F52',
      '--text-primary': '#0C1A18',
      '--text-secondary': '#1A3430',
      '--text-muted': '#345450',
      '--sidebar-bg': '#0a0a0c',
      '--sidebar-active': 'rgba(45,117,104,0.15)',
      '--sidebar-active-text': '#5ABFAE',
    },
    preview: 'bg-[#ACC4C0]',
    dot: 'bg-[#2D7568]',
  },

  // ── LEGACY (hidden) ───────────────────────────────────────

  light: {
    name: 'Light (Legacy)',
    hidden: true,
    colors: {
      '--color-obsidian': '237 233 226',
      '--color-charcoal': '244 241 235',
      '--bg-primary': '#EDE9E2',
      '--bg-secondary': '#F4F1EB',
      '--bg-card': '#F4F1EB',
      '--border': 'rgba(0,0,0,0.07)',
      '--accent': '#4A6741',
      '--accent-rgb': '74,103,65',
      '--accent-hover': '#3B5334',
      '--text-primary': '#2C2C2A',
      '--text-secondary': '#5E5D58',
      '--text-muted': '#8E8D86',
      '--sidebar-bg': '#E4E0D8',
      '--sidebar-active': 'rgba(74,103,65,0.1)',
      '--sidebar-active-text': '#4A6741',
    },
    preview: 'bg-[#EDE9E2]',
    dot: 'bg-[#4A6741]',
  },
  ivory: {
    name: 'Ivory (Legacy)',
    hidden: true,
    colors: {
      '--color-obsidian': '237 233 226',
      '--color-charcoal': '244 241 235',
      '--bg-primary': '#EDE9E2',
      '--bg-secondary': '#F4F1EB',
      '--bg-card': '#F4F1EB',
      '--border': 'rgba(0,0,0,0.07)',
      '--accent': '#4A6741',
      '--accent-rgb': '74,103,65',
      '--accent-hover': '#3B5334',
      '--text-primary': '#2C2C2A',
      '--text-secondary': '#5E5D58',
      '--text-muted': '#8E8D86',
      '--sidebar-bg': '#E4E0D8',
      '--sidebar-active': 'rgba(74,103,65,0.1)',
      '--sidebar-active-text': '#4A6741',
    },
    preview: 'bg-[#EDE9E2]',
    dot: 'bg-[#4A6741]',
  },
  midnight: {
    name: 'Midnight (Legacy)',
    hidden: true,
    colors: {
      '--color-obsidian': '10 14 26',
      '--color-charcoal': '19 24 37',
      '--bg-primary': '#0A0E1A',
      '--bg-secondary': '#131825',
      '--bg-card': '#131825',
      '--border': 'rgba(255,255,255,0.06)',
      '--accent': '#38BDF8',
      '--accent-rgb': '56,189,248',
      '--accent-hover': '#22D3EE',
      '--text-primary': '#FFFFFF',
      '--text-secondary': '#94A3B8',
      '--text-muted': '#64748B',
      '--sidebar-bg': '#131825',
      '--sidebar-active': 'rgba(56,189,248,0.1)',
      '--sidebar-active-text': '#38BDF8',
    },
    preview: 'bg-[#0A0E1A]',
    dot: 'bg-sky-400',
  },
  sunset: {
    name: 'Sunset (Legacy)',
    hidden: true,
    colors: {
      '--color-obsidian': '10 14 26',
      '--color-charcoal': '19 24 37',
      '--bg-primary': '#0A0E1A',
      '--bg-secondary': '#131825',
      '--bg-card': '#131825',
      '--border': 'rgba(255,255,255,0.06)',
      '--accent': '#38BDF8',
      '--accent-rgb': '56,189,248',
      '--accent-hover': '#22D3EE',
      '--text-primary': '#FFFFFF',
      '--text-secondary': '#94A3B8',
      '--text-muted': '#64748B',
      '--sidebar-bg': '#131825',
      '--sidebar-active': 'rgba(56,189,248,0.1)',
      '--sidebar-active-text': '#38BDF8',
    },
    preview: 'bg-[#0A0E1A]',
    dot: 'bg-sky-400',
  },
  arctic: {
    name: 'Arctic (Legacy)',
    hidden: true,
    colors: {
      '--color-obsidian': '246 244 241',
      '--color-charcoal': '253 252 250',
      '--bg-primary': '#F6F4F1',
      '--bg-secondary': '#FDFCFA',
      '--bg-card': '#FDFCFA',
      '--border': 'rgba(0,0,0,0.06)',
      '--accent': '#4A6741',
      '--accent-rgb': '74,103,65',
      '--accent-hover': '#3B5334',
      '--text-primary': '#2C2C2A',
      '--text-secondary': '#6B6B66',
      '--text-muted': '#9C9C96',
      '--sidebar-bg': '#F0EDE8',
      '--sidebar-active': 'rgba(74,103,65,0.08)',
      '--sidebar-active-text': '#4A6741',
    },
    preview: 'bg-[#F6F4F1]',
    dot: 'bg-[#4A6741]',
  },
  // High-contrast (added Phase 1): pure black surfaces, white body text and a
  // bright accent — for long shifts, bright rooms and low-vision users.
  contrast: {
    name: 'High Contrast',
    group: 'dark',
    shipped: true,
    colors: {
      '--color-obsidian': '0 0 0',
      '--color-charcoal': '10 10 10',
      '--bg-primary': '#000000',
      '--bg-secondary': '#0A0A0A',
      '--bg-card': '#0A0A0A',
      '--border': 'rgba(255,255,255,0.22)',
      '--accent': '#7DD3FC',
      '--accent-rgb': '125,211,252',
      '--accent-hover': '#BAE6FD',
      '--text-primary': '#FFFFFF',
      '--text-secondary': '#E4E4E7',
      '--text-muted': '#A1A1AA',
      '--sidebar-bg': '#000000',
      '--sidebar-active': 'rgba(125,211,252,0.18)',
      '--sidebar-active-text': '#7DD3FC',
    },
    preview: 'bg-black',
    dot: 'bg-sky-300',
  },
  // ── LIVE THEMES ──────────────────────────────────────────
  // Owner, 5 Sep 2026: "themes based on rain, snow, fog, tree leaves, sunrise,
  // sunset ocean". Six weather/daylight states, three light and three dark, so
  // the Live schedule below can hand the day from one to the next without ever
  // putting a dark theme on a bright desk or the reverse. `live: true` keeps
  // them in their own section of the picker instead of padding Night/Light.

  sunrise: {
    name: 'Sunrise',
    group: 'light',
    shipped: true,
    live: true,
    lightSidebar: true,
    blurb: 'First light rising — warm cream and terracotta',
    ambience: 'sunrise',
    colors: {
      '--color-obsidian': '251 242 233',
      '--color-charcoal': '255 250 244',
      '--bg-primary': '#FBF2E9',
      '--bg-secondary': '#FFFAF4',
      '--bg-card': '#FFFAF4',
      '--border': '#EADBC8',
      '--accent': '#C2632B',
      '--accent-rgb': '194,99,43',
      '--accent-hover': '#A24F1F',
      '--text-primary': '#2A1E14',
      '--text-secondary': '#5A4738',
      '--text-muted': '#7A6553',
      '--sidebar-bg': '#F6E9DA',
      '--sidebar-active': '#F0DAC4',
      '--sidebar-active-text': '#A24F1F',
    },
    preview: 'bg-[#FBF2E9]',
    dot: 'bg-[#C2632B]',
  },

  snowfall: {
    name: 'Snowfall',
    group: 'light',
    shipped: true,
    live: true,
    lightSidebar: true,
    blurb: 'Snow falling — cool white and ice blue',
    ambience: 'snow',
    colors: {
      '--color-obsidian': '237 242 248',
      '--color-charcoal': '251 253 255',
      '--bg-primary': '#EDF2F8',
      '--bg-secondary': '#FBFDFF',
      '--bg-card': '#FBFDFF',
      '--border': '#D3DEEA',
      '--accent': '#1F6FB2',
      '--accent-rgb': '31,111,178',
      '--accent-hover': '#185A93',
      '--text-primary': '#101A24',
      '--text-secondary': '#3B4A5A',
      '--text-muted': '#5B6C7D',
      '--sidebar-bg': '#E4ECF6',
      '--sidebar-active': '#D3E2F2',
      '--sidebar-active-text': '#185A93',
    },
    preview: 'bg-[#EDF2F8]',
    dot: 'bg-[#1F6FB2]',
  },

  fog: {
    name: 'Fog',
    group: 'light',
    shipped: true,
    live: true,
    lightSidebar: true,
    blurb: 'Fog rolling through — soft grey, low glare',
    ambience: 'fog',
    colors: {
      '--color-obsidian': '232 232 228',
      '--color-charcoal': '245 245 242',
      '--bg-primary': '#E8E8E4',
      '--bg-secondary': '#F5F5F2',
      '--bg-card': '#F5F5F2',
      '--border': '#D2D2CC',
      '--accent': '#3F6B6E',
      '--accent-rgb': '63,107,110',
      '--accent-hover': '#305457',
      '--text-primary': '#1C1F1E',
      '--text-secondary': '#474B49',
      '--text-muted': '#676C69',
      '--sidebar-bg': '#E2E2DD',
      '--sidebar-active': '#D4DCDA',
      '--sidebar-active-text': '#305457',
    },
    preview: 'bg-[#E8E8E4]',
    dot: 'bg-[#3F6B6E]',
  },

  sunset: {
    name: 'Sunset',
    group: 'dark',
    shipped: true,
    live: true,
    blurb: 'Sunset over water — the sun sinks, clouds drift',
    ambience: 'sunset',
    colors: {
      '--color-obsidian': '14 21 38',
      '--color-charcoal': '22 32 58',
      '--bg-primary': '#0E1526',
      '--bg-secondary': '#16203A',
      '--bg-card': '#16203A',
      '--border': 'rgba(255, 255, 255, 0.10)',
      '--accent': '#F2865E',
      '--accent-rgb': '242,134,94',
      '--accent-hover': '#F79E7B',
      '--text-primary': '#EAEEF7',
      '--text-secondary': '#BCC6DB',
      '--text-muted': '#98A4BE',
      '--sidebar-bg': '#0A1020',
      '--sidebar-active': 'rgba(242, 134, 94, 0.18)',
      '--sidebar-active-text': '#F79E7B',
    },
    preview: 'bg-[#0E1526]',
    dot: 'bg-[#F2865E]',
  },

  rainfall: {
    name: 'Rainfall',
    group: 'dark',
    shipped: true,
    live: true,
    blurb: 'Rain falling — wet slate and rain blue',
    ambience: 'rain',
    colors: {
      '--color-obsidian': '18 26 33',
      '--color-charcoal': '26 36 45',
      '--bg-primary': '#121A21',
      '--bg-secondary': '#1A242D',
      '--bg-card': '#1A242D',
      '--border': 'rgba(255, 255, 255, 0.09)',
      '--accent': '#6FB0DC',
      '--accent-rgb': '111,176,220',
      '--accent-hover': '#8AC3E8',
      '--text-primary': '#E6EFF5',
      '--text-secondary': '#B4C6D2',
      '--text-muted': '#8FA3B1',
      '--sidebar-bg': '#0E161C',
      '--sidebar-active': 'rgba(111, 176, 220, 0.18)',
      '--sidebar-active-text': '#8AC3E8',
    },
    preview: 'bg-[#121A21]',
    dot: 'bg-[#6FB0DC]',
  },

  foliage: {
    name: 'Foliage',
    group: 'dark',
    shipped: true,
    live: true,
    blurb: 'Leaves falling — dark olive and leaf gold',
    ambience: 'leaves',
    colors: {
      '--color-obsidian': '20 24 15',
      '--color-charcoal': '29 34 22',
      '--bg-primary': '#14180F',
      '--bg-secondary': '#1D2216',
      '--bg-card': '#1D2216',
      '--border': 'rgba(255, 255, 255, 0.09)',
      '--accent': '#A9C766',
      '--accent-rgb': '169,199,102',
      '--accent-hover': '#BCD880',
      '--text-primary': '#ECF0E2',
      '--text-secondary': '#C2CBAE',
      '--text-muted': '#A0AB8C',
      '--sidebar-bg': '#10140C',
      '--sidebar-active': 'rgba(169, 199, 102, 0.18)',
      '--sidebar-active-text': '#BCD880',
    },
    preview: 'bg-[#14180F]',
    dot: 'bg-[#A9C766]',
  },
  waterfall: {
    name: 'Waterfall',
    group: 'dark',
    shipped: true,
    live: true,
    ambience: 'waterfall',
    blurb: 'Water falling — deep teal rock, white spill and mist',
    colors: {
      '--color-obsidian': '12 26 29',
      '--color-charcoal': '20 37 42',
      '--bg-primary': '#0C1A1D',
      '--bg-secondary': '#14252A',
      '--bg-card': '#14252A',
      '--border': 'rgba(255, 255, 255, 0.09)',
      '--accent': '#58C7C9',
      '--accent-rgb': '88,199,201',
      '--accent-hover': '#74DADC',
      '--text-primary': '#E4F1F3',
      '--text-secondary': '#AFC9CE',
      '--text-muted': '#8AA7AD',
      '--sidebar-bg': '#091417',
      '--sidebar-active': 'rgba(88, 199, 201, 0.18)',
      '--sidebar-active-text': '#74DADC',
    },
    preview: 'bg-[#0C1A1D]',
    dot: 'bg-[#58C7C9]',
  },

  // ── LIVE THEMES · DAY VARIANTS ───────────────────────────
  // Owner, 6 Jun 2026: "can we make day and light version of each animated
  // theme?" — settled as: add the missing LIGHT versions of the four scenes
  // that only existed after dark, so nobody has to put a dark screen on a
  // bright desk to get the scene they like. Same four drawings, daylight
  // palettes (the `-day` ambience kinds in styles/ambience.css). The dark
  // counterparts of Sunrise, Snowfall and Fog were explicitly deferred.

  goldenhour: {
    name: 'Golden Hour',
    group: 'light',
    shipped: true,
    live: true,
    lightSidebar: true,
    blurb: 'Sunset in daylight — low gold sun, warm cream',
    ambience: 'sunset-day',
    colors: {
      '--color-obsidian': '253 246 232',
      '--color-charcoal': '255 252 243',
      '--bg-primary': '#FDF6E8',
      '--bg-secondary': '#FFFCF3',
      '--bg-card': '#FFFCF3',
      '--border': '#EEE0C4',
      '--accent': '#A8761C',
      '--accent-rgb': '168,118,28',
      '--accent-hover': '#8C5F12',
      '--text-primary': '#2A2114',
      '--text-secondary': '#57492F',
      '--text-muted': '#79684A',
      '--sidebar-bg': '#F7EDD8',
      '--sidebar-active': '#F0E0BE',
      '--sidebar-active-text': '#8C5F12',
    },
    preview: 'bg-[#FDF6E8]',
    dot: 'bg-[#A8761C]',
  },

  rainday: {
    name: 'Rain Day',
    group: 'light',
    shipped: true,
    live: true,
    lightSidebar: true,
    blurb: 'Rain in daylight — pale grey and wet light',
    ambience: 'rain-day',
    colors: {
      '--color-obsidian': '238 242 246',
      '--color-charcoal': '251 252 254',
      '--bg-primary': '#EEF2F6',
      '--bg-secondary': '#FBFCFE',
      '--bg-card': '#FBFCFE',
      '--border': '#D5DEE7',
      '--accent': '#2F6D96',
      '--accent-rgb': '47,109,150',
      '--accent-hover': '#245A7E',
      '--text-primary': '#101A22',
      '--text-secondary': '#3A4A57',
      '--text-muted': '#5C6C79',
      '--sidebar-bg': '#E5ECF2',
      '--sidebar-active': '#D5E2EC',
      '--sidebar-active-text': '#245A7E',
    },
    preview: 'bg-[#EEF2F6]',
    dot: 'bg-[#2F6D96]',
  },

  autumnday: {
    name: 'Autumn Day',
    group: 'light',
    shipped: true,
    live: true,
    lightSidebar: true,
    blurb: 'Leaves falling in daylight — warm amber and moss',
    ambience: 'leaves-day',
    colors: {
      '--color-obsidian': '252 244 228',
      '--color-charcoal': '255 251 241',
      '--bg-primary': '#FCF4E4',
      '--bg-secondary': '#FFFBF1',
      '--bg-card': '#FFFBF1',
      '--border': '#EDDFC2',
      '--accent': '#5E6B22',
      '--accent-rgb': '94,107,34',
      '--accent-hover': '#4A5619',
      '--text-primary': '#241F10',
      '--text-secondary': '#514A2E',
      '--text-muted': '#736B4A',
      '--sidebar-bg': '#F5EBD6',
      '--sidebar-active': '#E7E0BC',
      '--sidebar-active-text': '#4A5619',
    },
    preview: 'bg-[#FCF4E4]',
    dot: 'bg-[#5E6B22]',
  },

  waterfallday: {
    name: 'Waterfall Day',
    group: 'light',
    shipped: true,
    live: true,
    lightSidebar: true,
    blurb: 'The fall in daylight — bright teal and white water',
    ambience: 'waterfall-day',
    colors: {
      '--color-obsidian': '234 244 245',
      '--color-charcoal': '249 253 253',
      '--bg-primary': '#EAF4F5',
      '--bg-secondary': '#F9FDFD',
      '--bg-card': '#F9FDFD',
      '--border': '#CFE2E4',
      '--accent': '#14717A',
      '--accent-rgb': '20,113,122',
      '--accent-hover': '#0E5B62',
      '--text-primary': '#0C2226',
      '--text-secondary': '#35504F',
      '--text-muted': '#57706F',
      '--sidebar-bg': '#E1EEEF',
      '--sidebar-active': '#CFE4E5',
      '--sidebar-active-text': '#0E5B62',
    },
    preview: 'bg-[#EAF4F5]',
    dot: 'bg-[#14717A]',
  },

  // ── LIVE THEMES · NIGHT VARIANTS ─────────────────────────
  // Owner, 6 Jun 2026 (second pass): "also add the deferred dark/night versions
  // of Sunrise, Snowfall and Fog, so all 7 scenes exist in both day and night".
  // Same three drawings, night palettes (the `-night` ambience kinds in
  // styles/ambience.css). Every scene now has a light seat and a dark one.

  daybreak: {
    name: 'Daybreak',
    group: 'dark',
    shipped: true,
    live: true,
    blurb: 'Before dawn — the sun climbing out of an indigo sky',
    ambience: 'sunrise-night',
    colors: {
      '--color-obsidian': '18 16 34',
      '--color-charcoal': '27 24 48',
      '--bg-primary': '#121022',
      '--bg-secondary': '#1B1830',
      '--bg-card': '#1B1830',
      '--border': 'rgba(255, 255, 255, 0.10)',
      '--accent': '#F0A868',
      '--accent-rgb': '240,168,104',
      '--accent-hover': '#F7BE8C',
      '--text-primary': '#F5EFE7',
      '--text-secondary': '#C6BAAC',
      '--text-muted': '#A09486',
      '--sidebar-bg': '#0D0B1A',
      '--sidebar-active': 'rgba(240, 168, 104, 0.18)',
      '--sidebar-active-text': '#F7BE8C',
    },
    preview: 'bg-[#121022]',
    dot: 'bg-[#F0A868]',
  },

  snownight: {
    name: 'Snow Night',
    group: 'dark',
    shipped: true,
    live: true,
    blurb: 'Snow after dark — midnight blue, moonlit drifts',
    ambience: 'snow-night',
    colors: {
      '--color-obsidian': '13 21 33',
      '--color-charcoal': '22 32 46',
      '--bg-primary': '#0D1521',
      '--bg-secondary': '#16202E',
      '--bg-card': '#16202E',
      '--border': 'rgba(255, 255, 255, 0.10)',
      '--accent': '#8FC4EE',
      '--accent-rgb': '143,196,238',
      '--accent-hover': '#AFD8F7',
      '--text-primary': '#EAF2FA',
      '--text-secondary': '#B6C7D9',
      '--text-muted': '#93A4B6',
      '--sidebar-bg': '#0A1119',
      '--sidebar-active': 'rgba(143, 196, 238, 0.18)',
      '--sidebar-active-text': '#AFD8F7',
    },
    preview: 'bg-[#0D1521]',
    dot: 'bg-[#8FC4EE]',
  },

  fognight: {
    name: 'Night Fog',
    group: 'dark',
    shipped: true,
    live: true,
    blurb: 'Fog after dark — slate grey, mist catching the light',
    ambience: 'fog-night',
    colors: {
      '--color-obsidian': '22 25 27',
      '--color-charcoal': '31 36 38',
      '--bg-primary': '#16191B',
      '--bg-secondary': '#1F2426',
      '--bg-card': '#1F2426',
      '--border': 'rgba(255, 255, 255, 0.10)',
      '--accent': '#8FB3B5',
      '--accent-rgb': '143,179,181',
      '--accent-hover': '#A9C8C9',
      '--text-primary': '#E8EDED',
      '--text-secondary': '#B7C1C1',
      '--text-muted': '#96A3A3',
      '--sidebar-bg': '#111415',
      '--sidebar-active': 'rgba(143, 179, 181, 0.18)',
      '--sidebar-active-text': '#A9C8C9',
    },
    preview: 'bg-[#16191B]',
    dot: 'bg-[#8FB3B5]',
  },

};

/** The Live day arc. Every live theme gets an hour of its own, and the light /
 *  dark split follows real daylight so the screen never fights the room.
 *  Boundaries are LOCAL hours — the team works in one timezone. */
const LIVE_SCHEDULE = [
  { from: 5, to: 8, theme: 'sunrise', label: 'Sunrise' },
  { from: 8, to: 11, theme: 'snowfall', label: 'Morning' },
  { from: 11, to: 14, theme: 'waterfall', label: 'Midday' },
  { from: 14, to: 16, theme: 'fog', label: 'Afternoon' },
  { from: 16, to: 19, theme: 'sunset', label: 'Sunset' },
  { from: 19, to: 23, theme: 'rainfall', label: 'Evening' },
  { from: 23, to: 5, theme: 'foliage', label: 'Night' },
];

const liveSlotFor = (now = new Date()) => {
  const h = now.getHours();
  return LIVE_SCHEDULE.find(s => (s.from < s.to ? h >= s.from && h < s.to : h >= s.from || h < s.to))
    || LIVE_SCHEDULE[0];
};

/** When the current slot hands over, as a local HH:MM — shown in the picker so
 *  the theme changing under someone is announced, not magic. */
const liveNextChange = (now = new Date()) => {
  const slot = liveSlotFor(now);
  const d = new Date(now);
  d.setMinutes(0, 0, 0);
  d.setHours(slot.to);
  if (d <= now) d.setDate(d.getDate() + 1);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const ThemeContext = createContext();

const LIVE_KEY = 'hirojet-theme-live';
// Public and sign-in screens are dark by design — see the effect below.
const PUBLIC_THEME = 'obsidian';
const MOTION_KEY = 'hirojet-theme-motion';
const MIGRATIONS = {
  light: 'slate', ivory: 'clay', arctic: 'glacier', forest: 'sage',
  lagoon: 'glacier', aurora: 'cypress', midnight: 'obsidian',
  dusk: 'sunset', paper: 'clay', mist: 'fog', sand: 'clay',
};

export function ThemeProvider({ children }) {
  // Live mode is per-person and OFF by default — a theme that changes on its
  // own must be asked for, never sprung on someone (owner: "can be turned off
  // by anyone for themselves").
  const [live, setLive] = useState(() => {
    try { return localStorage.getItem(LIVE_KEY) === 'on'; } catch { return false; }
  });
  // The MANUAL choice. Live mode never overwrites it, so switching Live off
  // puts you back on the theme you actually picked.
  const [manual, setManual] = useState(() => {
    let saved = 'meadow';   // the day theme is the default for new users
    try { saved = localStorage.getItem('hirojet-theme') || 'meadow'; } catch { /* private mode */ }
    return MIGRATIONS[saved] || saved;
  });
  const [slot, setSlot] = useState(() => liveSlotFor());
  // Motion is ON by default — an animated theme with the animation off is just
  // a palette. Anyone who finds it distracting (or is on a tired laptop) turns
  // it off for themselves and keeps the colours. The OS-level
  // `prefers-reduced-motion` is honoured on top of this, in ambience.css.
  const [motion, setMotion] = useState(() => {
    try { return localStorage.getItem(MOTION_KEY) !== 'off'; } catch { return true; }
  });

  // Re-read the clock on a timer AND whenever the tab comes back, because a
  // laptop that slept through the 19:00 handover would otherwise sit on the
  // afternoon theme until the next tick.
  useEffect(() => {
    if (!live) return undefined;
    const tick = () => setSlot(liveSlotFor());
    tick();
    const id = setInterval(tick, 60000);
    window.addEventListener('focus', tick);
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(id);
      window.removeEventListener('focus', tick);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [live]);

  const themeName = live ? slot.theme : manual;

  // The marketing site and the staff sign-in screens are DRAWN on dark ink —
  // their copy, their glass panels and the logo lockup all assume it. A light
  // in-app theme used to follow the user out on logout: /teamlogin came up
  // washed out with white text on near-white and the wordmark disappeared
  // (owner, 6 Jun 2026, with a screenshot). Anything outside /app renders the
  // dark base; the person's own theme returns the moment they are back in the
  // app. /w/ is the client workspace and paints itself, so it is left alone,
  // and candidate token pages pin their own literals in index.css.
  const { pathname } = useLocation();
  const isStaffApp = pathname.startsWith('/app') || pathname.startsWith('/w/');
  const appliedTheme = isStaffApp ? themeName : PUBLIC_THEME;

  useEffect(() => {
    const theme = themes[appliedTheme];
    if (!theme) return;
    const root = document.documentElement;
    Object.entries(theme.colors).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });
    root.setAttribute('data-theme', appliedTheme);
    // Tokens (styles/tokens.css) branch on this: light surfaces need darker
    // hover steps and darker status colours to stay readable.
    root.setAttribute('data-theme-group', theme.group || 'dark');
    // Live themes have a scene behind the app, so their CARDS turn to glass —
    // otherwise the scenery is 90% hidden behind opaque surfaces and the theme
    // is just particles in the gutters. Only this flag switches that on, so
    // every ordinary theme keeps its solid surfaces (styles/ambience.css).
    if (theme.ambience && isStaffApp) root.setAttribute('data-ambient', 'on');
    else root.removeAttribute('data-ambient');
  }, [appliedTheme, isStaffApp]);

  useEffect(() => {
    try { localStorage.setItem(LIVE_KEY, live ? 'on' : 'off'); } catch { /* private mode */ }
  }, [live]);

  useEffect(() => {
    try { localStorage.setItem('hirojet-theme', manual); } catch { /* private mode */ }
  }, [manual]);

  useEffect(() => {
    try { localStorage.setItem(MOTION_KEY, motion ? 'on' : 'off'); } catch { /* private mode */ }
  }, [motion]);

  // Picking a theme by hand means you want THAT theme — so it also switches
  // Live off rather than being silently overwritten at the next hour.
  const setTheme = useCallback((key) => {
    setLive(false);
    setManual(key);
  }, []);

  const value = useMemo(() => ({
    themeName, setTheme, themes,
    live, setLive,
    motion, setMotion,
    liveSlot: slot,
    liveSchedule: LIVE_SCHEDULE,
    liveNextChange: liveNextChange(),
    manualTheme: manual,
    isAnimated: !!themes[themeName]?.ambience,
  }), [themeName, setTheme, live, motion, slot, manual]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}

export { themes };
